import { getAuth } from 'firebase/auth';
import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateErrorHeader,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';
import {
    ErrorBoundary,
    HTTPError,
    Logger,
    type SentryScope,
    SentryTags,
    showReportDialog,
} from '@gitbook/util-debug';

import { useHideSplashscreen } from '../Splashscreen';

const logger = Logger('app:crash');

enum CrashType {
    GENERIC = 'crash',
    NETWORK = 'network-crash',
}

/**
 * React error boundary to show a reload button when an error occured.
 */
export function ErrorScreen(props: { children: React.ReactNode }): React.ReactElement {
    const { children } = props;

    const onError = React.useCallback((error: Error, componentStack: string, eventId: string) => {
        logger.emergency(
            'An error occured during rendering:\n\n',
            error,
            '\n\nThe error is located at:\n',
            componentStack
        );

        analytics.logEvent({
            type: 'crash',
            crashType: getCrashType(error),
            sentryEvent: eventId,
        });
    }, []);

    return (
        <ErrorBoundary
            fallback={(args) => <ErrorScreenFallback {...args} />}
            onError={onError}
            beforeCapture={beforeCapture}
        >
            {children}
        </ErrorBoundary>
    );
}

function ErrorScreenFallback(props: { eventId: string | null; error: Error }): React.ReactElement {
    const { eventId, error } = props;

    const handleReload = () => {
        window.location.reload();
    };

    // Hide splashscreen if it crashes during the first render
    useHideSplashscreen();

    const renderMessage = () => {
        switch (getCrashType(error)) {
            case CrashType.NETWORK:
                return <NetworkScreenMessage onReload={handleReload} {...props} />;
            default:
                return (
                    <GeneralScreenMessage
                        onShareFeedback={eventId ? () => showReportDialog({ eventId }) : null}
                        onReload={handleReload}
                        {...props}
                    />
                );
        }
    };

    return (
        <div className="flex min-h-full w-full flex-col bg-base" data-testid="error.screen">
            {renderMessage()}
        </div>
    );
}

function GeneralScreenMessage(props: {
    onReload: () => void;
    onShareFeedback: (() => void) | null;
    error: Error;
}): React.ReactElement {
    const { onReload, onShareFeedback, error } = props;

    return (
        <EmptyState>
            <EmptyStateErrorHeader />
            <Description>
                Our team has been notified. If you'd like to help, you can describe what happened
                below.
            </Description>
            <EmptyStateActions>
                <Button label="Reload" kind="primary" size="medium" onPress={onReload} />
                {onShareFeedback && (
                    <Button
                        label="Let us know what happened"
                        kind="secondary"
                        size="medium"
                        onPress={onShareFeedback}
                    />
                )}
            </EmptyStateActions>
            {process.env.NODE_ENV === 'development' ? (
                <div className="mt-4">
                    <div>These additional informations are shown in development only.</div>
                    {error instanceof HTTPError ? (
                        <div>HTTP request failed with status {error.statusCode}</div>
                    ) : null}
                    <pre>{error.stack}</pre>
                </div>
            ) : null}
        </EmptyState>
    );
}

function NetworkScreenMessage(props: { onReload: () => void }): React.ReactElement {
    const { onReload } = props;

    const onAnchorButtonPress = () => {
        window.open('https://gitbook.com/docs/resources/firewall-safelist', '_blank');
    };

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.CloudOff />
            </EmptyStateIcon>
            <Heading>Connection lost</Heading>
            <Description>
                Your device might be offline, or GitBook might be experiencing issues.
            </Description>
            <EmptyStateActions>
                <Button
                    label="Reload"
                    kind="primary"
                    size="medium"
                    onPress={onReload}
                    className="mr-2"
                />
                <Button
                    kind="secondary"
                    label="Network troubleshooting"
                    size="medium"
                    leadingIcon={Icon.Link}
                    onPress={onAnchorButtonPress}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Annotate the sentry error to identify react crashes.
 */
function beforeCapture(scope: SentryScope, error: Error): void {
    const { currentUser } = getAuth();
    scope.setUser(
        currentUser
            ? {
                  id: currentUser.uid,
                  email: currentUser.email || undefined,
                  username: currentUser.displayName || undefined,
              }
            : null
    );

    switch (getCrashType(error)) {
        case CrashType.NETWORK:
            scope.setTag(SentryTags.ERROR_CATEGORY, 'network-crash');
            break;
        default:
            scope.setTag(SentryTags.ERROR_CATEGORY, 'react-crash');
    }
}

function getCrashType(error: Error): CrashType {
    if (error.name === 'FirebaseError' && 'code' in error && error.code === 'unavailable') {
        return CrashType.NETWORK;
    }

    return CrashType.GENERIC;
}
